import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import axiosInstance from "../../config/axiosApi/axiosApi";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice";
import { appSettings } from "../../app/appSettingsSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import "./../order/ManageOrderFilters.css";

const ManageReservations = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState([]);
  const [originalReservationsCount, setOriginalReservationsCount] = useState(0);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  

  const pageKey = "reservations";
  const defaultVisibleColumns = {
    customerName: true,
    dateTime: true,
    guests: true,
    tableNo: true,
    vip: true,
    occasion: true,
    status: true,
  };
  
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "reserv_DateTime", label: "Date & Time" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;

  const sortByOptions = [
    { value: "reserv_DateTime", label: "Date & Time" },
    { value: "reserv_CustomerName", label: "Customer Name" },
    { value: "reserv_TableNo", label: "Table No" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  const fetchReservationsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        "/api/TableReservation/GetAllTableReservations",
        {
          params: {
            CompID,
            Search: search?.trim() || "",
          },
        }
      );
      // Response logged

      const reservationsData = response.data.data || response.data;

      if (Array.isArray(reservationsData)) {
        setReservations(reservationsData);
        if (!search.trim()) {
          setOriginalReservationsCount(reservationsData.length);
        }
      } else {
        // Unexpected API response format silently
        setReservations([]);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      // Error fetching reservations data silently
      showErrorToast("Failed to load reservations");
    }
  }, [search, CompID]);
  // CompID logged

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
  }, [dispatch, pageKey]);

  useEffect(() => {
    if (CompID) {
      const handler = setTimeout(() => {
        fetchReservationsData();
      }, 1000);
      return () => clearTimeout(handler);
    }
  }, [fetchReservationsData, CompID]);

  const handleDeleteReservation = async (reservation) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete reservation for ${reservation.reserv_CustomerName}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const response = await axiosInstance.delete(
        "/api/Reservation/DeleteReservation",
        {
          params: {
            ID: reservation.reserv_ID,
            CompID,
          },
        }
      );

      if (response.data.status) {
        setReservations((prevReservations) =>
          prevReservations.filter((r) => r.reserv_ID !== reservation.reserv_ID)
        );
        showSuccessToast("Reservation deleted successfully");
      } else {
        showErrorToast(response.data.message || "Deletion failed");
      }
      setLoading(false);
    } catch (err) {
      // Error in handleDeleteReservation silently
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };

  const handleEditReservation = (reservation) => {
    navigate(`/reservation/updateReservation/${reservation.reserv_ID}`);
  };

  const toggleReservationStatus = async (id, currentStatus) => {
    try {
      const updatedReservations = reservations.map((reservation) =>
        reservation.reserv_ID === id
          ? { ...reservation, reserv_Status: currentStatus === "Active" ? "Cancelled" : "Active" }
          : reservation
      );
      setReservations(updatedReservations);

      const response = await axiosInstance.post(
        "/api/Reservation/UpdateReservationStatus",
        {
          CompID,
          ID: id,
          Reserv_Status: currentStatus === "Active" ? "Cancelled" : "Active",
        }
      );

      if (!response.data.status) {
        showErrorToast(
          response.data.message || "Failed to update reservation status"
        );
        setReservations(reservations); // Revert to original state
      }
    } catch (error) {
      // Status update failed silently
      showErrorToast("Failed to update reservation status");
      setReservations(reservations); // Revert to original state
    }
  };

  const renderNoDataMessage = () => {
    if (search.trim() && reservations.length === 0) {
      return (
        <div className="alert alert-info text-center">
          No reservations found matching your search criteria.
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Reservations</li>
          </ol>
          <h1 className="page-header mb-0">Manage Reservations</h1>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active" id="allTab">
            {/* Enhanced Filter Section */}
            <div className="mb-4">
              <div
                className="card filter-card"
                style={{
                  position: "relative",
                  zIndex: 1,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: `0 4px 20px ${themeColor}40`
                }}
              >
                <div className="card-body p-3">
                  {/* Filter Controls */}
                  <div className="row g-3 align-items-center">
                    {/* Search Field */}
                    <div className="col-xl-4 col-lg-4 col-md-6">
                      <div className="search-input filter-control">
                        <input
                          type="text"
                          className="filter-input"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search by customer name, table..."
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            color: '#333'
                          }}
                        />
                      </div>
                    </div>

                    {/* Sort By Dropdown */}
                    <div className="col-xl-3 col-lg-3 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={sortBy}
                          onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                          options={sortByOptions}
                          styles={dropdownStyles}
                          placeholder="Sort By"
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuShouldBlockScroll={false}
                          menuShouldScrollIntoView={false}
                          closeMenuOnScroll={false}
                        />
                      </div>
                    </div>

                    {/* Sort Order Dropdown */}
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <SortOrderDropDownSelection
                          defaultValue={sortOrder}
                          onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="col-xl-3 col-lg-3 col-md-12">
                      <div className="d-flex gap-2">
                        <button
                          className="clear-btn"
                          onClick={clearFilters}
                          title="Clear all filters"
                        >
                          <i className="fa fa-times me-1"></i>
                          Filter
                        </button>

                        <ColumnSelection
                          visibleColumns={visibleColumns}
                          setVisibleColumns={(cols) =>
                            dispatch(setVisibleColumns({ pageKey, columns: cols }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {renderNoDataMessage()}
            <div className="table-responsive" style={{ minHeight: "200px" }}>
              <table className="table table-hover">
                <thead 
                  className="bg-light"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    {visibleColumns.customerName && (
                      <th className="pt-2 pb-2">Customer Name</th>
                    )}
                    {visibleColumns.dateTime && (
                      <th className="pt-2 pb-2">Date & Time</th>
                    )}
                    {visibleColumns.guests && (
                      <th className="pt-2 pb-2 text-center">Guests</th>
                    )}
                    {visibleColumns.tableNo && (
                      <th className="pt-2 pb-2 text-center">Table No</th>
                    )}
                    {visibleColumns.vip && (
                      <th className="pt-2 pb-2 text-center">VIP</th>
                    )}
                    {visibleColumns.occasion && (
                      <th className="pt-2 pb-2 text-center">Occasion</th>
                    )}
                    {visibleColumns.status && (
                      <th className="pt-2 pb-2 text-center">Status</th>
                    )}
                    <th className="pt-2 pb-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((reservation) => (
                    <tr key={reservation.reserv_ID}>
                      {visibleColumns.customerName && (
                        <td
                          className="align-middle"
                          onClick={() => handleEditReservation(reservation)}
                          style={{ cursor: "pointer" }}
                        >
                          {reservation.reserv_CustomerName}
                        </td>
                      )}
                      {visibleColumns.dateTime && (
                        <td
                          className="align-middle"
                          onClick={() => handleEditReservation(reservation)}
                          style={{ cursor: "pointer" }}
                        >
                          {new Date(reservation.reserv_DateTime).toLocaleString()}
                        </td>
                      )}
                      {visibleColumns.guests && (
                        <td
                          className="align-middle text-center"
                          onClick={() => handleEditReservation(reservation)}
                          style={{ cursor: "pointer" }}
                        >
                          {reservation.reserv_NoOfGuests || "N/A"}
                        </td>
                      )}
                      {visibleColumns.tableNo && (
                        <td
                          className="align-middle text-center"
                          onClick={() => handleEditReservation(reservation)}
                          style={{ cursor: "pointer" }}
                        >
                          {reservation.reserv_TableNos || "N/A"}
                        </td>
                      )}
                      {visibleColumns.vip && (
                        <td
                          className="align-middle text-center"
                          onClick={() => handleEditReservation(reservation)}
                          style={{ cursor: "pointer" }}
                        >
                          {reservation.isVIPStatus ? "Yes" : "No"}
                        </td>
                      )}
                      {visibleColumns.occasion && (
                        <td
                          className="align-middle text-center"
                          onClick={() => handleEditReservation(reservation)}
                          style={{ cursor: "pointer" }}
                        >
                          {reservation.occasionType || "N/A"}
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td className="align-middle text-center">
                          <div
                            className={`toggle-switch ${
                              reservation.reserv_Status === "Active" ? "active" : ""
                            }`}
                          >
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={reservation.reserv_Status === "Active"}
                                onChange={() =>
                                  toggleReservationStatus(
                                    reservation.reserv_ID,
                                    reservation.reserv_Status
                                  )
                                }
                              />
                              <span className="slider round"></span>
                            </label>
                          </div>
                        </td>
                      )}
                      <td className="align-middle text-center">
                        <div className="d-flex justify-content-center gap-2">
                          {/* Edit Icon */}
                          <button
                            onClick={() => handleEditReservation(reservation)}
                            className="btn btn-sm btn-outline-primary"
                            title="Edit Reservation"
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '6px',
                              border: `1px solid ${themeColor}`,
                              color: themeColor,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = themeColor;
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = themeColor;
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                          </button>

                          {/* Delete Icon */}
                          <button
                            onClick={() => handleDeleteReservation(reservation)}
                            className="btn btn-sm btn-outline-danger"
                            title="Delete Reservation"
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '6px',
                              border: '1px solid #dc3545',
                              color: '#dc3545',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#dc3545';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#dc3545';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageReservations;